import React, { useState, useEffect, useRef } from 'react';
import { Scan, X, Loader2 } from 'lucide-react';

/**
 * BarcodeScanner Component
 * Usa l'input della tastiera per leggere codici a barre da scanner USB/Bluetooth
 * La maggior parte degli scanner barcode emulano una tastiera e inviano i caratteri + ENTER
 */
export const BarcodeScanner = ({ onScan, onClose, placeholder = "Scansiona o digita il codice..." }) => {
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef(null);
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Focus automatico sull'input
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Listener globale per catturare input da scanner barcode
    const handleKeyDown = (e) => {
      // Gli scanner inviano caratteri molto velocemente
      // Resettiamo il buffer dopo 50ms di inattività
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (e.key === 'Enter' && bufferRef.current.length >= 3) {
        // Codice completo ricevuto
        e.preventDefault();
        setScanning(true);
        const scannedCode = bufferRef.current;
        bufferRef.current = '';
        setCode(scannedCode);
        
        // Trigger callback
        setTimeout(() => {
          onScan(scannedCode);
          setScanning(false);
        }, 300);
      } else if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // Carattere singolo - aggiungi al buffer
        bufferRef.current += e.key;
      }

      // Reset buffer dopo 50ms
      timeoutRef.current = setTimeout(() => {
        bufferRef.current = '';
      }, 50);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onScan]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (code.trim().length >= 3) {
      setScanning(true);
      onScan(code.trim());
      setTimeout(() => setScanning(false), 300);
    }
  };

  return (
    <div className="bg-gradient-to-r from-sky-50 to-sky-50 rounded-xl p-4 border-2 border-dashed border-sky-300">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-sky-100 rounded-lg">
          <Scan className="w-5 h-5 text-sky-600" />
        </div>
        <div>
          <h4 className="font-medium text-sky-800">Scanner Codice a Barre</h4>
          <p className="text-xs text-sky-600">Scansiona con lettore USB/Bluetooth o digita manualmente</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto p-1 hover:bg-sky-100 rounded"
          >
            <X className="w-4 h-4 text-sky-600" />
          </button>
        )}
      </div>

      <form onSubmit={handleManualSubmit} className="flex gap-2">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={placeholder}
            className="w-full px-4 py-3 border-2 border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-lg font-mono"
            data-testid="barcode-input"
          />
          {scanning && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={code.trim().length < 3 || scanning}
          className="px-4 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="barcode-submit"
        >
          <Scan className="w-5 h-5" />
        </button>
      </form>

      <div className="mt-3 flex items-center gap-4 text-xs text-sky-600">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Scanner attivo
        </span>
        <span>Formati supportati: EAN-13, EAN-8, Code 128, QR Code</span>
      </div>
    </div>
  );
};

export default BarcodeScanner;
